import { Inject, Injectable, Optional } from '@angular/core';
import { BulkActionInstanceStatusInterface } from '@vendasta/bulk-actions';
import { Observable, SchedulerLike } from 'rxjs';
import { map, publishReplay, refCount, takeWhile } from 'rxjs/operators';
import { ActionListsApiService, HistoryItemGetter } from '../action-lists.api.service';
import { ActionListHistoryItem } from '../action-lists.api.service.response';
import { ActionListStatuses } from './action-lists-history.store.service';
import { ProgressReporter, withPolling } from './progress-reporter.service';

@Injectable({ providedIn: 'root' })
export class HistoryItemsProgressReporter implements ProgressReporter {
  constructor(
    @Inject(ActionListsApiService)
    private readonly actionListsApi: HistoryItemGetter,
    @Optional() @Inject('') readonly scheduler?: SchedulerLike,
    @Optional() @Inject('') readonly pollInterval?: number,
  ) {
    this.pollInterval = pollInterval || 5000;
    this.scheduler = scheduler || undefined; // rxjs can't handle null
  }

  getPolledStatus$(instanceId: string): Observable<BulkActionInstanceStatusInterface> {
    function isInProgress(v: ActionListHistoryItem): boolean {
      switch (v.status) {
        case ActionListStatuses.IN_PROGRESS:
        case ActionListStatuses.RETRYING:
        case ActionListStatuses.NOT_STARTED:
          return true;
        case ActionListStatuses.COMPLETED:
        case ActionListStatuses.FAILED:
          return false;
      }
      console.error('Unexpected action status: ', v.status);
      return true;
    }

    return withPolling(() => this.actionListsApi.getHistoryItem(instanceId), this.pollInterval, this.scheduler).pipe(
      takeWhile((v) => isInProgress(v), true),
      map(convertToStatus),
      publishReplay(1),
      refCount(),
    );
  }
}

function convertToStatus(item: ActionListHistoryItem): BulkActionInstanceStatusInterface {
  const errs = item.errors || {};
  const errorMessages = Object.keys(errs);
  const errorCounts = errorMessages.map((key) => errs[key]);
  const totalErrs = errorCounts.reduce((p, v) => p + v, 0);
  const processedItems = item.totalAccounts + totalErrs;
  const isFinalized = item.status === ActionListStatuses.COMPLETED;
  const totalItems = isFinalized ? processedItems : item.totalRows;
  return {
    instanceId: item.itemId,
    totalItems: totalItems,
    processedItems: processedItems,
    errors: errorMessages,
    actionDisplayName: item.title,
    isFinalized: isFinalized,
  };
}
